import React, {useContext, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {classes, style} from './doppe-viewer-actions-list.st.css';
import {DoppeDtoAction, DoppeDtoActionViewType} from '../../client-server-common/types/doppe-dto-action';
import {
    ListItemsAnimator,
    ListView,
    listViewCreateDataSource,
    listViewFindListViewItemElement,
    ListViewSupportedHtmlAttributes,
    ModalsScopeContext,
    useListViewItemRootAttributes,
    useRenderState
} from '@wix/devzai-utils-react';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {FlexLayout} from '@wix/devzai-common-client';
import {
    BymoActionsMode,
    bymoActionsResolvePrimaryAction
} from '../../client-server-common/bymo-pages/bymo-actions-settings';
import {arrayFind, assertNotNullable, evaluateFunction, uiPartsGroupCreateSelector} from '@wix/devzai-utils-common';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {useBymoPageProps} from '../../client-server-common';
import {DoppeViewerActionContext} from '../doppe-viewer-action-context/doppe-viewer-action-context';
import {DoppeViewerAction} from '../doppe-viewer-action/doppe-viewer-action';
import {
    useDoppeViewerAppUiServiceComponentRef
} from '../../client/doppe-viewer-app-client-services/doppe-viewer-app-ui-service';
import {elementAnimateWithClassAnimation, elementVerticallyScrollToInScrollParent} from '@wix/devzai-utils-dom';
import {DoppeViewerActionViewUiPartsGroups} from '../doppe-viewer-action-view/doppe-viewer-action-view';
import {
    doppeActionButtonTemplateInstanceHasShadow
} from '../../client-server-common/doppe-templates/doppe-action-button-template-utils';
import {DoppeViewerModalsController} from "../doppe-viewer-modals-controller/doppe-viewer-modals-controller";
import {useBymoPageContextProps} from "../../doppe-sdk";
import {DoppeViewerDivider, DoppeViewerDividerSkin} from "../doppe-viewer-divider/doppe-viewer-divider";

export const DoppeViewerActionsList = React.memo(function DoppeViewerActionsList(props: DoppeViewerActionsList.Props) {
    const {
        renderActionView,
        supportsRenderingFramelessWidgets = false,
        fullScreenModeTogglingController,
        disableItemsAnimation = false,

        className,
        ...htmlAttributes
    } = props;

    const {
        actions,
        actionsSettings,
        actionButtonTemplate
    } = useBymoPageProps();

    const viewerContext = useBymoPageViewerContext();
    const clientServices = useDoppeViewerClientServices();

    const actionsMode = actionsSettings.actionsMode;

    const visibleActions = useMemo(() => {
        return evaluateFunction(() => {
            if (actionsSettings.actionsMode !== BymoActionsMode.SingleAction) {
                return actions.filter(action => action.enabled);
            } else {
                const primaryAction = bymoActionsResolvePrimaryAction(actionsSettings, actions);

                return primaryAction ? [primaryAction] : []
            }
        })
    }, [actions, actionsSettings])

    const actionsListViewDataSource = useMemo(() => {
        return listViewCreateDataSource<DoppeDtoAction>(visibleActions, {
            idFunction: item => item.id
        })

    }, [visibleActions]);

    const [expandedActionId, setExpandedActionId] = useState<DoppeDtoAction.Id>()

    const actionsListViewRef = useRef<ListView<DoppeDtoAction>>(null);
    const actionsListRef = useDoppeViewerAppUiServiceComponentRef('pageActionsList');

    const modalsScope = assertNotNullable(useContext(ModalsScopeContext));

    const bymoPageContextProps = useBymoPageContextProps();

    const renderState = useRenderState({
        actionButtonTemplate: actionButtonTemplate,
        createActionController: (actionId: DoppeDtoAction.Id) => {
            const [action, actionIndex] = arrayFind(visibleActions, action => action.id === actionId);

            if (action) {
                return new DoppeViewerActionContext<any>({
                    action: action,
                    actionIndex: actionIndex,
                    clientServices: clientServices,
                    viewerContextData: viewerContext,
                    bymoPageContextProps: bymoPageContextProps,
                    actionTogglingController: actionListController,
                    fullScreenModeTogglingController: fullScreenModeTogglingController,
                    modalsController: new DoppeViewerModalsController({
                        modalsScope: modalsScope
                    })
                })
            } else {
                return null;
            }
        }
    })

    const actionListController = useMemo<DoppeViewerActionsList>(() => {
        const actionListController = {
            toggleActionView(actionId, isExpanded) {
                if (isExpanded) {
                    setExpandedActionId(actionId);
                } else {
                    setExpandedActionId(currentExpandedActionId => {
                        if (currentExpandedActionId === actionId) {
                            return undefined;
                        } else {
                            return currentExpandedActionId;
                        }
                    })
                }
            },
            async highlightAction (actionId) {
                const actionsListView = actionsListViewRef.current;

                const actionListViewItemElement = actionsListView ?
                    (listViewFindListViewItemElement(actionsListView, actionId) ?? null) :
                    null;

                if (actionListViewItemElement) {

                    await elementVerticallyScrollToInScrollParent(actionListViewItemElement, {
                        marginTop: 24,
                        scrollBehaviour: 'smooth'
                    });

                    const highlightWithShadow = !doppeActionButtonTemplateInstanceHasShadow(renderState.current.actionButtonTemplate);

                    actionListViewItemElement.querySelectorAll(
                        uiPartsGroupCreateSelector(DoppeViewerActionViewUiPartsGroups.ActionFrameElement)
                    )
                        .forEach(element => {
                            elementAnimateWithClassAnimation(element, highlightWithShadow ? classes.highlightAnimationWithShadow : classes.highlightAnimation)
                        })
                }
            },

            async scrollToAction (actionId) {
                const actionsListView = actionsListViewRef.current;

                const actionListViewItemElement = actionsListView ?
                    (listViewFindListViewItemElement(actionsListView, actionId) ?? null) :
                    null;

                if (actionListViewItemElement) {
                    await elementVerticallyScrollToInScrollParent(actionListViewItemElement, {
                        marginTop: 24,
                        scrollBehaviour: 'smooth'
                    });
                }
            },

            async scrollToActionsListContainer () {
                const actionsListView = actionsListViewRef.current;

                const actionListViewItemElement = actionsListView ?
                    (actionsListView.getRootElement() ?? null) :
                    null;

                if (actionListViewItemElement) {

                    await elementVerticallyScrollToInScrollParent(actionListViewItemElement, {
                        marginTop: 48,
                        scrollBehaviour: 'smooth'
                    });
                }
            },

            getActionElementByActionId (actionId) {

                const actionsListView = actionsListViewRef.current;
                if (actionsListView) {
                    return listViewFindListViewItemElement(actionsListView, actionId) ?? null;
                } else {
                    return null;
                }
            },

            getActionController (actionId) {
                return renderState.current.createActionController(actionId);
            }
        } as DoppeViewerActionsList;

        return actionListController;
    }, [renderState]);

    useImperativeHandle(actionsListRef, () => actionListController, [actionListController])

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, className)}
        >
            <ListView
                ref={actionsListViewRef}
                isFocusable={false}
                className={style(classes.actionsListView, FlexLayout.column.default)}
                itemsWrapper={!disableItemsAnimation && viewerContext.mode === BymoPageViewerMode.LivePreview && actionsMode !== BymoActionsMode.SingleAction ?
                    (items) => {
                        return (
                            <ListItemsAnimator
                                appearTransition={{
                                    animation: [
                                        {
                                            transform: `scale(0.9)`,
                                            opacity: '0'
                                        },
                                        {
                                            transform: `scale(1)`,
                                            opacity: '1'
                                        }
                                    ],
                                    durationMilliseconds: 300
                                }}
                                disappearTransition={{
                                    animation: [
                                        {
                                            transform: `scale(1)`,
                                            opacity: '1'
                                        },
                                        {
                                            transform: `scale(0.9)`,
                                            opacity: '0'
                                        }
                                    ],
                                    durationMilliseconds: 300,
                                }}
                            >
                                {items}
                            </ListItemsAnimator>
                        )
                    } : undefined
                }
                renderItem={renderProps => {

                    const actionDto = renderProps.dataItem;
                    const actionItemIndex = renderProps.itemIndex;

                    const nextAction = actionItemIndex < actionsListViewDataSource.length - 1 ? actionsListViewDataSource[actionItemIndex + 1].dataItem : null;

                    const shouldRenderDividerAfterAction = evaluateFunction(() => {

                        if (!nextAction) {
                            return false;
                        }

                        if (actionDto.viewType === DoppeDtoActionViewType.InPageFrameless) {
                            return true;
                        }


                        if (nextAction) {
                            if (nextAction.viewType === DoppeDtoActionViewType.InPageFrameless) {
                                return true;
                            }
                        }

                        return false;
                    })

                    // TODO:
                    //  I'm not sure why the actionIndex value is incremented by 1.
                    //  Therefore I added an additional property actionIndexInList.
                    return (
                        <>
                            <ListViewItemView
                                actionDto={actionDto}
                                actionIndex={actionItemIndex + 1}
                                actionIndexInList={actionItemIndex}
                                actionListController={actionListController}
                                renderActionView={renderActionView}
                                isExpanded={expandedActionId === actionDto.id}
                                fullScreenModeTogglingController={fullScreenModeTogglingController}
                                supportsRenderingFramelessWidgets={supportsRenderingFramelessWidgets}
                            />
                            {shouldRenderDividerAfterAction ? (
                                <DoppeViewerDivider
                                    className={style(classes.onPageActionsDivider, {
                                        beforeFramedAction: nextAction !== null && nextAction.viewType !== DoppeDtoActionViewType.InPageFrameless,
                                        afterFramedAction: actionDto.viewType !== DoppeDtoActionViewType.InPageFrameless,
                                    })}
                                    skin={DoppeViewerDividerSkin.HorizontalLine}
                                />
                            ) : null}
                        </>
                    )
                }}
            >
                {actionsListViewDataSource}
            </ListView>
        </div>
    );
});

export interface DoppeViewerActionsList extends DoppeViewerActionContext.ActionTogglingController {

    highlightAction: (actionId: DoppeDtoAction.Id) => Promise<void>;

    scrollToAction: (actionId: DoppeDtoAction.Id) => Promise<void>;

    scrollToActionsListContainer: () => Promise<void>;

    getActionElementByActionId: (actionId: DoppeDtoAction.Id) => Element | null;

    getActionController: (actionId: DoppeDtoAction.Id) => DoppeViewerActionContext<any> | null;
}

export namespace DoppeViewerActionsList {

    export interface Props extends ListViewSupportedHtmlAttributes {
        renderActionView: DoppeViewerAction.ActionViewRenderFunction;
        supportsRenderingFramelessWidgets?: boolean;
        fullScreenModeTogglingController?: DoppeViewerActionContext.FullScreenModeTogglingController;
        disableItemsAnimation?: boolean;
    }
}

const ListViewItemView = React.memo(function ListViewItemView (
    props: {
        actionDto: DoppeDtoAction;
        actionIndex: number;
        actionIndexInList: number;
        actionListController: DoppeViewerActionsList;
        isExpanded: boolean;
    } & Pick<DoppeViewerActionsList.Props, 'renderActionView' | 'supportsRenderingFramelessWidgets' | 'fullScreenModeTogglingController'>
) {
    const {
        actionDto,
        renderActionView,
        supportsRenderingFramelessWidgets,
        actionIndex,
        actionIndexInList,
        actionListController,
        fullScreenModeTogglingController,
        isExpanded,
    } = props;

    const clientServices = useDoppeViewerClientServices();
    const doppeActionTypesStore = clientServices.actionTypesStore;
    const actionType = doppeActionTypesStore.getRegisteredActionType(actionDto.actionTypeId);
    const listViewItemRootAttributes = useListViewItemRootAttributes();

    if (actionType === undefined) {
        return (
            <div>{`Action type with id '${actionDto.actionTypeId}' isn't registered.`}</div>
        )
    } else {
        return (
            <DoppeViewerAction
                action={actionDto}
                renderActionView={renderActionView}
                supportsRenderingFramelessWidgets={supportsRenderingFramelessWidgets}
                isExpanded={isExpanded}
                actionIndex={actionIndex}
                actionTogglingController={actionListController}
                actionIndexInList={actionIndexInList}
                fullScreenModeTogglingController={fullScreenModeTogglingController}
                actionViewRootHtmlAttributes={listViewItemRootAttributes}
            />
        )
    }
});